import { useMemo, useCallback } from 'react';

import { UserVerificationStatusEnum } from 'src/common/constants';
import { useAppSelector } from 'src/hooks';
import { RootState } from 'src/setup';
import { Symbol, FutureSymbol } from 'src/types';
import { FeatureArrayKey, PlatformType } from 'src/types/user';

interface KycAndFeaturesConfigReturnValue {
    allowOnboarding: boolean;
    allowTrading: boolean;
    allowDeposit: boolean;
    allowWithdrawal: boolean;
    showPopup: boolean;
    earnPlatformEnabled: boolean;
    convertPlatformEnabled: boolean;
    tradeSpotPlatformEnabled: boolean;
    p2pPlatformEnabled: boolean;
    tradeDerivativesPlatformEnabled: boolean;
    isSymbolEnabled: (value: string) => boolean;
    enabledSymbols: string[] | null | undefined;
    enabledSpotSymbols: Symbol[];
    enabledDerivativesSymbols: FutureSymbol[];
    userKycConfigLoading?: boolean;
    userFeaturesConfigLoading?: boolean;
    userVerifyInfoLoading?: boolean;
}

export const useKycAndFeaturesConfig = (): KycAndFeaturesConfigReturnValue => {
    const { userKycConfig, userFeaturesConfig, userVerifyInfo, loading } = useAppSelector(
        (state: RootState) => state.user,
    );
    const spotSymbols = useAppSelector((state) => state.global.configV2?.symbol) ?? {};
    const futuresSymbols = useAppSelector((state) => state.global.configV2?.futuresSymbol) ?? {};

    const kycVerified = userVerifyInfo?.verifyStatus === UserVerificationStatusEnum.VERIFIED;
    const userFeaturesConfigLoading = loading.fetchUserFeaturesConfig;
    const userVerifyInfoLoading = loading.fetchUserVerifyInfo;
    const userKycConfigLoading = loading.fetchUserKycConfig;

    const checkPermission = (key: keyof typeof userKycConfig): boolean => {
        if (kycVerified) return true;

        const value = userKycConfig[key];
        if (typeof value !== 'boolean') return true;
        return value;
    };

    const extractKycConfigSetting = (key: string) => {
        const rawValue = userKycConfig?.settings?.[key];
        return typeof rawValue === 'string'
            ? (rawValue as string).toLowerCase() === 'true'
            : rawValue ?? true;
    };

    const checkFeature = (key: FeatureArrayKey, value: PlatformType): boolean => {
        const featureList = userFeaturesConfig[key] as string[] | undefined;
        return !featureList || featureList.includes(value);
    };

    const filterSymbols = <T extends { symbolId: string }>(
        allSymbols: T[],
        enabledSymbols: string[],
        filterFn: (symbol: string) => boolean,
    ): T[] => {
        const filteredEnabled = enabledSymbols.filter(filterFn);
        if (filteredEnabled.length === 0) return allSymbols;
        return allSymbols.filter((s) => filteredEnabled.includes(s.symbolId));
    };

    const allowDeposit = checkPermission('allowDeposit');
    const allowOnboarding = checkPermission('allowOnboarding');
    const allowTrading = checkPermission('allowTrading');
    const allowWithdrawal = checkPermission('allowWithdrawal');

    const showPopup = extractKycConfigSetting('showPopup');

    const earnPlatformEnabled = checkFeature('platform', 'EARN');
    const convertPlatformEnabled = checkFeature('platform', 'C2C');
    const p2pPlatformEnabled = checkFeature('platform', 'P2P');
    const tradeSpotPlatformEnabled = checkFeature('platform', 'SPOT');
    const tradeDerivativesPlatformEnabled = checkFeature('platform', 'FUTURES');

    const isSymbolEnabled = useCallback(
        (symbol?: string) => {
            if (
                userFeaturesConfigLoading ||
                !Object.prototype.hasOwnProperty.call(userFeaturesConfig, 'symbols') ||
                userFeaturesConfig.symbols === null
            ) {
                return true;
            }

            return userFeaturesConfig.symbols.includes(symbol);
        },
        [userFeaturesConfig, userFeaturesConfigLoading],
    );

    const enabledSpotSymbols = useMemo(() => {
        if (userFeaturesConfigLoading) return spotSymbols;
        const spotSymbolsNormalized = Object.keys(spotSymbols).map(
            (key) => spotSymbols[key] as Symbol,
        );
        return filterSymbols(
            spotSymbolsNormalized,
            userFeaturesConfig?.symbols ?? [],
            (s) => !s.includes('SWAP'),
        );
    }, [spotSymbols, userFeaturesConfig?.symbols, userFeaturesConfigLoading]);

    const enabledDerivativesSymbols = useMemo(() => {
        if (userFeaturesConfigLoading) return spotSymbols;
        const futuresSymbolsNormalized = Object.keys(futuresSymbols).map(
            (key) => futuresSymbols[key] as FutureSymbol,
        );
        return filterSymbols(futuresSymbolsNormalized, userFeaturesConfig?.symbols ?? [], (s) =>
            s.includes('SWAP'),
        );
    }, [futuresSymbols, userFeaturesConfig?.symbols, userFeaturesConfigLoading]);

    return {
        allowDeposit,
        allowOnboarding,
        allowTrading,
        allowWithdrawal,
        showPopup,
        earnPlatformEnabled,
        convertPlatformEnabled,
        tradeSpotPlatformEnabled,
        tradeDerivativesPlatformEnabled,
        p2pPlatformEnabled,
        isSymbolEnabled,
        userFeaturesConfigLoading,
        userKycConfigLoading,
        userVerifyInfoLoading,
        enabledSymbols: userFeaturesConfig?.symbols,
        enabledSpotSymbols: enabledSpotSymbols as Symbol[],
        enabledDerivativesSymbols: enabledDerivativesSymbols as FutureSymbol[],
    };
};
